////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. FILE HEADER [WHITE #FFFFFF]
// ⚪ Contact Pod for Taytrom Publishing Co - Container component with form logic and state

// III. IMPORTS [GREEN #00FF00]
// 🟢 Third-party dependencies
import { useState, useEffect, FC } from 'react';

// 🟢 First-party components
import ContactPea from '../pea/TaytromPublishingCo_Kings_Home_SRC_Contact_Lobby_Pea.js';
import { taytromPubHomeFrontendLogger } from '../../../logger/TaytromPublishingCo_Kings_Home_SRC_Logger.js';

// IV. INDEPENDENT CONSTANTS [TEAL #00FFFF]
// 🔵 Component configuration
interface PodContactProps {
  className?: string;
}

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  company?: string;
  message?: string;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

// V. FUNCTION/CLASS NAME [BLUE #0000FF] - OUTPUT DECLARATION
// 🔵 Contact Pod Component
const Pod_Contact: FC<PodContactProps> = ({ className }) => {
  // VI. DECLARATIONS [PURPLE #FF00FF] - REQUIREMENTS SETUP
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitMessage, setSubmitMessage] = useState<string>('');

  // VII. INPUT DECLARATIONS [CYAN #00FFFF]
  useEffect(() => {
    taytromPubHomeFrontendLogger.info('Contact Pod mounted');

    return () => {
      taytromPubHomeFrontendLogger.debug('Contact Pod unmounted');
    };
  }, []);

  // VIII. OPERATIONS [ORANGE #FFA500] - REQUIREMENTS PROCESSING
  const validateEmail_VSubVSoVSt_V_L_DVT_R_IRO = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField_VSubVSoVSt_V_L_DVT_R_IRO = (
    fieldName: keyof FormData,
    value: string
  ): string | undefined => {
    switch (fieldName) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return undefined;

      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!validateEmail_VSubVSoVSt_V_L_DVT_R_IRO(value)) return 'Please enter a valid email address';
        return undefined;

      case 'company':
        if (!value.trim()) return 'Company is required';
        if (value.trim().length < 2) return 'Company must be at least 2 characters';
        return undefined;

      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 10) return 'Message must be at least 10 characters';
        return undefined;

      default:
        return undefined;
    }
  };

  const handleFieldChange_VSubVSoVSt_T_L_DVT_R_IRO = (
    fieldName: keyof FormData,
    value: string
  ): void => {
    // Update form data
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear error for this field if it exists
    if (formErrors[fieldName]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }

    // Real-time validation on blur (after user starts typing)
    if (value.length > 0) {
      const error = validateField_VSubVSoVSt_V_L_DVT_R_IRO(fieldName, value);
      if (error) {
        setFormErrors(prev => ({
          ...prev,
          [fieldName]: error
        }));
      }
    }
  };

  const handleFieldBlur_VSubVSoVSt_T_L_DVT_R_IRO = (
    fieldName: keyof FormData
  ): void => {
    const value = formData[fieldName];
    const error = validateField_VSubVSoVSt_V_L_DVT_R_IRO(fieldName, value);

    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: error
      }));
    }
  };

  const validateForm_VSubVSoVSt_V_L_DVT_R_IRO = (): boolean => {
    const errors: FormErrors = {};

    Object.keys(formData).forEach(key => {
      const fieldName = key as keyof FormData;
      const error = validateField_VSubVSoVSt_V_L_DVT_R_IRO(fieldName, formData[fieldName]);
      if (error) {
        errors[fieldName] = error;
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit_VSubVSoVSt_T_L_DVT_R_IRO = async (): Promise<void> => {
    taytromPubHomeFrontendLogger.info('Contact form submission initiated', {
      name: formData.name,
      email: formData.email,
      company: formData.company,
      messageLength: formData.message.length
    });

    // Validate all fields
    if (!validateForm_VSubVSoVSt_V_L_DVT_R_IRO()) {
      taytromPubHomeFrontendLogger.warn('Contact form validation failed', { errors: formErrors });
      return;
    }

    setSubmitStatus('submitting');
    setSubmitMessage('');

    try {
      // TODO: Backend integration with Resend API
      // For now, simulate successful submission
      await new Promise(resolve => setTimeout(resolve, 1500));

      taytromPubHomeFrontendLogger.info('Contact form submitted successfully', {
        name: formData.name,
        email: formData.email,
        company: formData.company
      });

      setSubmitStatus('success');
      setSubmitMessage('Thank you for your message! We\'ll get back to you within 24 hours.');

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          message: ''
        });
        setSubmitStatus('idle');
        setSubmitMessage('');
      }, 3000);

    } catch (error) {
      taytromPubHomeFrontendLogger.error('Contact form submission failed', { error });
      setSubmitStatus('error');
      setSubmitMessage('Sorry, something went wrong. Please try again or email us directly at contact@taylorpublishing.co');
    }
  };

  // IX. PRE-RENDER [RED #FF0000] - REQUIREMENTS ASSEMBLY
  const podState = {
    formData,
    formErrors,
    submitStatus,
    submitMessage,
    onFieldChange: handleFieldChange_VSubVSoVSt_T_L_DVT_R_IRO,
    onFieldBlur: handleFieldBlur_VSubVSoVSt_T_L_DVT_R_IRO,
    onSubmit: handleSubmit_VSubVSoVSt_T_L_DVT_R_IRO
  };

  // X. RENDER/EXPORTS [BLACK #000000] - OUTPUT DELIVERY
  return <ContactPea {...podState} className={className} />;
};

export default Pod_Contact;
